import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import getMetadata from '../api/product/getMetadata';
import viewProductVariants from '../api/product/viewProductVariants';
import viewProducts from '../api/product/viewProducts';
import RequestBody from '../components/dataDisplay/DataGridTable/types/requestBody.type';
import Product from '../types/products/product.type';
import Response from '../types/response';
import removeDuplicatesArray from '../utils/removeDublicatesArray';
import useRedirectLogin from './useRedirectLogin';

type GetVariantsRequestBodyArgs = {
  metadataKeys: string[];
  searchValue?: string;
  searchObject?: RequestBody;
  searchFilters?: Record<string, unknown>;
};

const getVariantsRequestBody = ({
  metadataKeys,
  searchValue,
  searchObject,
  searchFilters,
}: GetVariantsRequestBodyArgs) => {
  const searchColumns = metadataKeys.map((key) => ({
    [key]: {
      cop: ['ilike'],
      op: 'or',
      value: searchValue,
    },
  }));

  return {
    columns: ['product_id'],
    page_size: 40000,
    astype: 'unique_list',
    search: searchObject?.search || searchColumns,
    ...searchFilters,
  };
};

type Args = {
  searchValue?: string;
  searchObject?: RequestBody;
  shouldPreventSearch?: boolean;
  searchFilters?: Record<string, unknown>;
};

const useSearchProducts = ({
  searchValue,
  searchObject,
  shouldPreventSearch = false,
  searchFilters,
}: Args) => {
  const redirectLogin = useRedirectLogin();

  const [metadataKeys, setMetadataKeys] = useState<string[] | null>(null);
  const [productsResponse, setProductsResponse] = useState<Response<
    Product[]
  > | null>(null);

  const searchValueRef = useRef<Args['searchValue']>(undefined);
  const searchObjectRef = useRef<Args['searchObject']>(undefined);
  const searchFiltersRef = useRef<Args['searchFilters']>(undefined);

  const [loading, setLoading] = useState(false);

  const handleFetchMetadata = useCallback(async () => {
    const response = await getMetadata(redirectLogin);

    if (response.success) {
      const definitionsFiltered = removeDuplicatesArray(
        response.data.metadata_definitions,
        'metadata_key',
      );

      const keys = definitionsFiltered.map((item) => item.metadata_key);
      setMetadataKeys(keys);

      return keys;
    }
  }, [redirectLogin]);

  const handleFetchVariants = useCallback(
    async (requestBody: Record<string, unknown>) => {
      const response = await viewProductVariants({
        body: requestBody,
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        return response.data;
      }
    },
    [redirectLogin],
  );

  const handleFetchProducts = useCallback(
    async (productIds: string[], searchObject?: RequestBody) => {
      const response = await viewProducts({
        body: {
          ...searchObject,
          search: [
            {
              product_id: {
                cop: ['in'],
                value: productIds,
              },
            },
          ],
        },
        onUnauthorized: redirectLogin,
      });

      setProductsResponse(response);
    },
    [redirectLogin],
  );

  useEffect(() => {
    const getProducts = async (
      searchObject: Args['searchObject'],
      searchValue: Args['searchValue'],
      searchFilters: Args['searchFilters'],
    ) => {
      setLoading(true);

      const keys = metadataKeys ? metadataKeys : await handleFetchMetadata();

      if (keys) {
        const variantsRequestBody = getVariantsRequestBody({
          metadataKeys: keys,
          searchValue,
          searchObject,
          searchFilters,
        });

        const variants = await handleFetchVariants(variantsRequestBody);

        if (variants) {
          const productIds = variants as unknown as string[];
          await handleFetchProducts(productIds, searchObject);
        }

        setLoading(false);
      }
    };

    if (
      !isEqual(searchValue, searchValueRef.current) ||
      !isEqual(searchObject, searchObjectRef.current) ||
      !isEqual(searchFilters, searchFiltersRef.current)
    ) {
      searchValueRef.current = searchValue;
      searchObjectRef.current = searchObject;
      searchFiltersRef.current = searchFilters;

      if (shouldPreventSearch === false) {
        void getProducts(searchObject, searchValue, searchFilters);
      }
    }
  }, [
    handleFetchMetadata,
    handleFetchProducts,
    handleFetchVariants,
    metadataKeys,
    searchObject,
    searchValue,
    shouldPreventSearch,
    searchFilters,
  ]);

  return {
    productsResponse,
    tableColumnsList: metadataKeys,
    loading,
  };
};

export default useSearchProducts;
